import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import AvatarEditor from 'react-avatar-editor'
import IcomoonReact from 'icomoon-react'
import { useIsMobileQuery } from 'src/hooks/useMediaQuery'
import { useGlobalContext } from 'src/stores/GlobalContext'
import useUploadPhoto from 'src/queries/currentUser/useUploadPhoto'
import Button from 'src/components/Elements/Button'
import generalStyles from 'src/assets/styles/generalStyles.module.scss'
import fontStyles from 'src/assets/styles/fontStyles.module.scss'
import variables from 'src/assets/styles/variables.module.scss'
import settingStyles from '../settingStyles.module.scss'
import { useNavigate } from 'react-router-dom'
import useUploadVirtualPhoto from 'src/queries/virtualPortal/useUploadVirtualPhoto'
import rebrandIconSet from 'src/assets/icons/rebrandIconList.json'
import iconSet from 'src/assets/icons/iconList.json'

const ImageEditor = ({ selectedImage, isWizard, editorRef }) => {
  const navigate = useNavigate()
  const { currentUser } = useGlobalContext()
  const { mutate } = useUploadPhoto()
  const { mutate: virtualPortalMutate } = useUploadVirtualPhoto()
  const internalEditorRef = useRef(null) // Internal ref
  const refToUse = editorRef || internalEditorRef // Use the passed ref or fallback to internal ref
  const isMobileSize = useIsMobileQuery()
  const imageToEdit = selectedImage || currentUser.photo
  const width = isMobileSize ? 200 : 300
  const height = isMobileSize ? 200 : 300
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 })
  const [rotate, setRotation] = useState(0)
  const [image, setImage] = useState(imageToEdit)

  const handleCrop = async () => {
    const img = refToUse?.current?.getImageScaledToCanvas()?.toDataURL()
    setImage(img)
  }

  const saveBlob = async () => {
    const canvas = refToUse?.current?.getImageScaledToCanvas()
    return new Promise((resolve) => {
      return canvas?.toBlob((file) => {
        file.name = `${currentUser?.id}_profile_image.jpeg`
        file.lastModifiedDate = new Date()
        resolve(file)
      }, 'image/jpeg')
    })
  }

  const handleSaveImage = async () => {
    await handleCrop()
    const blob = await saveBlob()
    const formData = new FormData()
    formData.append('file', blob)
    if (isWizard) virtualPortalMutate(formData)
    else mutate(formData)
  }

  return (
    <div className={settingStyles.imageEditor}>
      <div className={generalStyles.pb24}>
        <AvatarEditor
          ref={refToUse} // Use the ref
          image={image}
          scale={parseFloat(scale)}
          width={width}
          height={height}
          position={position}
          onPositionChange={(pos) => setPosition(pos)}
          rotate={parseFloat(rotate)}
          borderRadius={300}
          border={0}
        />
      </div>
      <div
        className={classNames(
          generalStyles.flexColumn,
          generalStyles.centeredText
        )}
      >
        <div className={settingStyles.rangeContainer}>
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={'Control-SubtractIcon-XL-WhiteOutline'}
            color={variables.darkGrey}
            className={settingStyles.addIcon}
          />
          <input
            id="scale"
            name="scale"
            type="range"
            onChange={(e) => setScale((2 - parseFloat(e.target.value)).toFixed(2))}
            min="0"
            max="2"
            step="0.01"
            defaultValue="1"
          />
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={'Control-AddIcon-XL-WhiteOutline'}
            color={variables.darkGrey}
            className={settingStyles.minusIcon}
          />
        </div>
        <div className={classNames(generalStyles.mt20, generalStyles.mb20)}>
          <span className={fontStyles.pxsmallboldlsssinneutral08onyxgartnersans12pt}>Zoom</span>
        </div>
      </div>
      <div
        className={classNames(
          generalStyles.flexColumn,
          generalStyles.flexCentered
        )}
        style={{ alignItems: 'center' }}
      >
        {isWizard && (
          <Button
            color="solar"
            label="Cancel"
            size="large"
            onClick={() => navigate(-1)}
            style={{ marginBottom: '10px', width: '91px' }}
          />
        )}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '59px' }}>
          <Button
            size={'small'}
            label={'Rotate'}
            color={isWizard ? 'solar' : 'lightBase'}
            onClick={() => setRotation(rotate - 90)}
            iconRight={
              <IcomoonReact
                iconSet={rebrandIconSet}
                color={'white'}
                size={12}
                icon="Refresh"
                style={{ marginBottom: 1 }}
              />
            }
          />
        </div>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Button
            size={'small'}
            label={'Save'}
            color={isWizard ? 'solar' : 'lightBase'}
            onClick={handleSaveImage}
          />
        </div>
      </div>
    </div>
  )
}

export default ImageEditor
